import type { GetStaticPaths, GetStaticProps } from "next"
import { type Metadata, PageMeta } from "@/components/resources/components/Seo/PageMeta"
import { getAnnouncementTeasers } from "@/components/resources/data/beehiiv/teasers"
import {
  isResourceCategory,
  RESOURCE_CATEGORIES,
  type ResourceCategory,
  type ResourceCategorySlug,
} from "@/components/resources/data/markdown/categories"
import { byNewest, getCategoryTeasers, getPost } from "@/components/resources/data/markdown/posts"
import type { Teaser } from "@/components/resources/data/posts.types"
import { SiteLayout } from "@/components/resources/layouts/site"
import { CategoryArchive } from "@/components/resources/templates/CategoryArchive"
import {
  ChangelogCategoryArchive,
  type ChangelogEntry,
} from "@/components/resources/templates/ChangelogCategoryArchive"

interface ArchiveProps {
  title: string
  lead: string
  meta: Metadata
}

// The changelog shows the whole posts on the archive page, the other categories show teasers
type Props = ArchiveProps &
  (
    | { category: "changelog"; entries: ChangelogEntry[] }
    | { category: Exclude<ResourceCategorySlug, "changelog">; entries: Teaser[] }
  )

const Archive = (props: Props) => {
  const { title, lead, meta, category } = props
  return (
    <>
      <PageMeta meta={meta} />
      {props.category === "changelog" ? (
        <ChangelogCategoryArchive
          entries={props.entries}
          category={category}
          title={title}
          lead={lead}
        />
      ) : (
        <CategoryArchive entries={props.entries} category={category} title={title} lead={lead} />
      )}
    </>
  )
}

Archive.getLayout = SiteLayout

export default Archive

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: Object.keys(RESOURCE_CATEGORIES).map((category) => ({ params: { category } })),
  fallback: false,
})

export const getStaticProps: GetStaticProps<Props, { category: string }> = async ({ params }) => {
  const category = params?.category
  if (!category || !isResourceCategory(category)) {
    return { notFound: true }
  }

  const { title, lead, metaDescription }: ResourceCategory = RESOURCE_CATEGORIES[category]
  const shared: ArchiveProps = {
    title,
    lead,
    meta: {
      title,
      description: metaDescription ?? lead,
      canonical: `https://tokenterminal.com/resources/${category}`,
    },
  }

  if (category === "changelog") {
    const entries: ChangelogEntry[] = []
    for (const teaser of await getCategoryTeasers(category)) {
      const post = await getPost(category, teaser.slug)
      if (post) {
        entries.push({ ...teaser, mdxSource: post.mdxSource })
      }
    }
    return { props: { ...shared, category, entries }, revalidate: 180 }
  }

  let entries = await getCategoryTeasers(category)
  if (category === "articles") {
    entries = [...entries, ...(await getAnnouncementTeasers())].sort(byNewest)
  }
  return { props: { ...shared, category, entries }, revalidate: 180 }
}
